import React, { useState, useEffect } from 'react';
import { useBranding } from '../../hooks/useBranding';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import QRCode from 'qrcode';

interface QRCodeModalProps {
  open: boolean;
  onClose: () => void;
}

export const QRCodeModal: React.FC<QRCodeModalProps> = ({ open, onClose }) => {
  const [qrCodeDataURL, setQrCodeDataURL] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const branding = useBranding();

  // Get the current domain for the registration URL
  const registrationUrl = `${window.location.origin}/auth?mode=signup`;

  useEffect(() => {
    if (open) {
      generateQRCode();
    }
  }, [open]);

  const generateQRCode = async () => {
    try {
      const qrDataURL = await QRCode.toDataURL(registrationUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M'
      });
      setQrCodeDataURL(qrDataURL);
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(registrationUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };

  const handleDownloadQR = () => {
    if (qrCodeDataURL) {
      const link = document.createElement('a');
      link.download = 'tapestry-registration-qr.png';
      link.href = qrCodeDataURL;
      link.click();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="h6">Invite Family Members</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Share this QR code or link to invite family members to join <span style={{ color: '#3a9397', fontWeight: 'bold' }}>{branding.part1}</span><span style={{ color: '#ff9800', fontWeight: 'bold' }}>{branding.part2}</span>:
          </Typography>

          {qrCodeDataURL && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <img
                src={qrCodeDataURL}
                alt="Registration QR Code"
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '16px',
                  backgroundColor: 'white'
                }}
              />
            </Box>
          )}

          <Box sx={{
            bgcolor: 'grey.100',
            borderRadius: 1,
            p: 2,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                mr: 1,
                flexGrow: 1
              }}
            >
              {registrationUrl}
            </Typography>
            <Tooltip title={copied ? 'Copied!' : 'Copy URL'}>
              <IconButton
                onClick={handleCopyUrl}
                size="small"
                color={copied ? 'success' : 'default'}
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Family members can scan this QR code with their phone camera or visit the link above to create their account.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleDownloadQR}
          startIcon={<DownloadIcon />}
          variant="outlined"
          disabled={!qrCodeDataURL}
        >
          Download QR Code
        </Button>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};